/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { PriceComponent } from '@msdyn365-commerce/components';
import { ICoreContext, ITelemetry } from '@msdyn365-commerce/core';
import { ICheckoutState, IGiftCardExtend } from '@msdyn365-commerce/global-state';
import { Cart, CartLine } from '@msdyn365-commerce/retail-proxy';
import { format } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export interface IInvoiceSummaryProps {
    orderTotalLabel: string;
    invoiceLabel: string;
    loyaltyLabel?: string;
    giftcardLabel?: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    cart: Cart;
    id: string;
    typeName: string;
    freeText?: string;
    toBeCalculatedText?: string;
    checkoutState?: ICheckoutState;
}

export interface IInvoiceSummaryLines {
    orderTotal: React.ReactNode;
    invoices: React.ReactNode;
    giftCard?: React.ReactNode;
    loyalty?: React.ReactNode;
}

interface IInvoiceSummaryLineProps {
    label: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    price?: number;
    freeText?: string;
    toBeCalculatedText?: string;
    id: string;
    typeName: string;
    cssLabel: string;
}

const InvoiceSummaryLine: React.FC<IInvoiceSummaryLineProps> = ({
    price,
    label,
    context,
    id,
    typeName,
    toBeCalculatedText,
    freeText,
    cssLabel
}) => {
    return (
        <p className={`msc-invoice-summary__line-${cssLabel}`}>
            <span className="msc-invoice-summary__label">{label}</span>
            {price || price === 0 ? (
                <PriceComponent
                    data={{
                        price: { CustomerContextualPrice: price }
                    }}
                    freePriceText={freeText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    className="msc-invoice-summary__value"
                />
            ) : (
                <span className="msc-invoice-summary__value">{toBeCalculatedText}</span>
            )}
        </p>
    );
};

const _buildPaymentSummarySection = (
    props: IInvoiceSummaryProps,
    price: number | undefined,
    label: string,
    cssLabel: string
): React.ReactNode => {
    return (
        <InvoiceSummaryLine
            price={price}
            label={label}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            toBeCalculatedText={props.toBeCalculatedText}
            freeText={props.freeText}
            telemetry={props.telemetry}
            cssLabel={cssLabel}
        />
    );
};

const _computedLoyaltyAmount = (checkoutState: ICheckoutState): number => {
    return (checkoutState && checkoutState.loyaltyAmount) || 0;
};

const _computeGiftCardAmount = (checkoutState: ICheckoutState, cart: Cart): number => {
    const giftCardCounter = (count: number, giftCard: IGiftCardExtend) => {
        return count + (giftCard.Balance || 0);
    };
    const giftCardTotalValue = (checkoutState.giftCardExtends || []).reduce(giftCardCounter, 0);
    const amount = (cart.TotalAmount || 0) - _computedLoyaltyAmount(checkoutState);
    return Math.min(giftCardTotalValue, amount);
};

export const InvoiceSummary = (props: IInvoiceSummaryProps) => {
    const { TotalAmount, CartLines } = props.cart;
    const invoiceLines = CartLines?.filter(cartLine => cartLine.IsInvoiceLine);
    const reactNodes: IInvoiceSummaryLines = {
        invoices: invoiceLines?.map((invoiceLine: CartLine) => {
            return _buildPaymentSummarySection(
                props,
                invoiceLine.InvoiceAmount,
                format(props.invoiceLabel, invoiceLine.InvoiceId),
                'invoice'
            );
        }),
        orderTotal: _buildPaymentSummarySection(props, TotalAmount, props.orderTotalLabel, 'total')
    };

    if (props.checkoutState) {
        const giftCardAmount = _computeGiftCardAmount(props.checkoutState, props.cart);
        const loyaltyAmount = _computedLoyaltyAmount(props.checkoutState);
        const totalAmountAfterGiftCard = (TotalAmount || 0) - giftCardAmount - loyaltyAmount;

        if (loyaltyAmount > 0) {
            reactNodes.loyalty = _buildPaymentSummarySection(props, -loyaltyAmount, props.loyaltyLabel || 'Loyalty amount', 'loyalty');
        }

        if (giftCardAmount > 0) {
            reactNodes.giftCard = _buildPaymentSummarySection(
                props,
                -giftCardAmount,
                props.giftcardLabel || 'Gift card amount',
                'gift-card'
            );
        }

        reactNodes.orderTotal = _buildPaymentSummarySection(
            { ...props, freeText: undefined },
            totalAmountAfterGiftCard,
            props.orderTotalLabel,
            'total'
        );
    }

    return reactNodes;
};
